import { colors } from "colors"
import { fonts } from "fonts/config"
import React from "react"
import styled, { css } from "styled-components/macro"

const HeaderStyled = styled.header`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  background-color: ${colors.green};
  color: ${colors.tan};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.5);
  z-index: 2;
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: var(--map-width);
  padding: 0 10px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`

const HeaderSVGStyled = styled.svg`
  height: 50px;
  width: 50px;
`

const TitleAndTagline = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.2;
  margin-left: 7px;
`

const Title = styled.div`
  font-size: 25px;
  font-weight: ${fonts.weight.medium};
`

const Tagline = styled.div`
  font-size: 15px;
  font-weight: ${fonts.weight.light};
`

const AboutUsTab = styled.div<{ isAboutUsShowing: boolean }>`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  padding: 0 15px;
  color: ${colors.lightTan};
  font-weight: ${fonts.weight.medium};
  background-color: ${colors.orange};
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.5);
  transition: color 200ms ease-in, box-shadow 200ms ease-in;
  cursor: pointer;

  &:hover {
    color: ${colors.tan};
  }

  &::before {
    position: absolute;
    right: 0;
    left: 0;
    bottom: -20px;
    height: 20px;
    content: "";
  }

  ${({ isAboutUsShowing }) =>
    isAboutUsShowing &&
    css`
      box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.7);
    `}
`

export const Header: React.FC<{ isAboutUsShowing: boolean; toggleAboutUs: () => void }> = ({
  isAboutUsShowing,
  toggleAboutUs,
}) => (
  <HeaderStyled>
    <HeaderContent>
      <TitleContainer>
        <HeaderSVG />
        <TitleAndTagline>
          <Title>Vamos Gordo</Title>
          <Tagline>Our journey to a new home</Tagline>
        </TitleAndTagline>
      </TitleContainer>
      <AboutUsTab isAboutUsShowing={isAboutUsShowing} onClick={toggleAboutUs}>
        About Us
      </AboutUsTab>
    </HeaderContent>
  </HeaderStyled>
)

const HeaderSVG: React.FC = () => (
  <HeaderSVGStyled
    preserveAspectRatio="xMidYMid meet"
    data-bbox="20 20 160.001 160"
    viewBox="20 20 160.001 160"
    role="img"
  >
    <title>Vamos Gordo</title>
    <g>
      <path
        d="M180 99.975C179.986 55.793 144.158 19.986 99.975 20S19.986 55.842 20 100.025 55.842 180.014 100.025 180c24.544-.03 46.488-11.111 61.157-28.523.311-.223.644-.413.937-.664l-.191-.223c11.273-13.779 18.049-31.379 18.073-50.566l-.001-.049zM55.739 62.808a5.565 5.565 0 1111.108 0V73.88c-.92-.2-1.854-.361-2.808-.46V62.39a2.463 2.463 0 00-4.926 0v10.998a25.829 25.829 0 00-3.222.472l-.152-11.052zm4.146 14.657a2.445 2.445 0 001.692.688c.627 0 1.185-.253 1.62-.639 10.553.905 18.758 9.536 19.168 20.073H40.404c.427-10.793 8.928-19.376 19.481-20.122zm10.755 24.949h26.97v14.901h-.197c.018.495.119.963.197 1.439v23.241h-.123c.012.303.09.584.123.881v22.614l-20.541-43.52-12.191 22.961-16.479-34.907-14.069 26.438c-5.643-10.139-8.998-21.718-9.392-34.049l45.702.001zm-3.029 47.709l9.212-17.365 19.96 42.283a74.978 74.978 0 01-19.273-3.353l-10.274-21.763.375.198zm34.827 1.291c2.211 1.688 4.96 2.697 7.956 2.601l.12.005c4.34.158 8.237-1.923 10.595-5.204 2.349 3.264 6.222 5.338 10.541 5.199l.071.003c4.486.172 8.48-2.06 10.823-5.524a12.426 12.426 0 009.743 5.454c-12.987 12.577-30.49 20.502-49.848 21.114l-.001-23.648zm50.936-2.227a7.685 7.685 0 01-8.054-7.192h-5.542a8.104 8.104 0 01-16.084 0h-5.172a8.103 8.103 0 01-7.162 7.162c-4.408.488-8.371-2.661-8.922-7.047v-15.739c1.95 1.335 4.305 2.111 6.847 2.028l.169.007c3.851.135 7.307-1.656 9.495-4.49 2.189 2.822 5.633 4.609 9.473 4.483l.094.004c3.817.143 7.245-1.609 9.448-4.39 2.187 2.763 5.584 4.511 9.375 4.386l.169.007c3.795.133 7.198-1.611 9.391-4.372 2.188 2.749 5.576 4.489 9.356 4.365a12.014 12.014 0 003.55-.541 75.1 75.1 0 01-11.957 20.107 8.603 8.603 0 01-4.474 1.222zm19.036-29.035a7.142 7.142 0 01-6.055 3.419c-3.553.191-6.589-2.433-6.99-5.93.007-.111.041-.214.044-.326h-5.025c.004.099.033.19.039.288a7.08 7.08 0 01-6.151 5.922c-3.819.452-7.272-2.226-7.833-6.005.004-.07.026-.135.028-.205h-5c.003.074.026.142.03.216a7.083 7.083 0 01-6.165 5.994 7.094 7.094 0 01-7.879-6.21h-5.024a7.093 7.093 0 01-6.21 6.21c-3.736.443-7.108-2.117-7.78-5.765v-15.346h72.672a75.056 75.056 0 01-2.701 17.738zm-15.963-47.074l7.352-12.74a74.78 74.78 0 018.011 17.469l-6.757 3.911a32.831 32.831 0 00-8.606-8.64zm9.414 24.508h-55.527c.865-14.987 13.485-26.659 28.585-26.21 14.543.433 26.129 11.951 26.942 26.21zm-63.423-54.998l17.317 30.347a32.823 32.823 0 00-8.681 8.626l-8.636-4.986V42.588zm33.325 24.89a32.411 32.411 0 00-11.799 3.093l-21.526-37.723V24.94c12.036.392 23.362 3.59 33.325 8.994v33.544zM102.438 82.15l6.227 3.595a32.41 32.41 0 00-3.198 11.841h-3.03l.001-15.436zm68.104 15.436a32.407 32.407 0 00-3.113-11.665l5.683-3.289a75.26 75.26 0 011.997 14.953l-4.567.001zm-9.748-41.698l-8.533 14.786a32.404 32.404 0 00-11.67-3.179v-30.68a75.608 75.608 0 0120.203 19.073zM97.611 24.889v72.698H87.114c-.589-9.892-6.714-18.281-15.34-22.097V62.808c.017-.337.017-.674 0-1.011-.228-4.69-3.549-8.455-7.882-9.534v-4.849h2.783v-4.827h-2.783v-2.759h-4.827v2.759h-2.759v4.827h2.759v4.78c-4.815 1.104-8.303 5.507-8.054 10.614V75.43c-8.692 3.79-14.872 12.216-15.464 22.156h-10.61c1.24-39.591 33.086-71.445 72.674-72.697zM37.256 141.281l10.872-20.493 22.916 48.536c-13.863-5.8-25.61-15.639-33.788-28.043z"
        fill="#E6D1BE"
        data-color="1"
      />
    </g>
  </HeaderSVGStyled>
)
